class FuelCalculatorWidget extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.config = this.parseConfig();
    this.stepDefinitions = this.normalizeStepDefinitions(
      this.config.steps || this.config.stepConfig,
    );
    //console.log('Fuel calculator step definitions:', this.stepDefinitions,this.config.steps);
    this.stepSelections = this.stepDefinitions.reduce((accumulator, step) => {
      accumulator[step.key] = null;
      return accumulator;
    }, {});

    this.state = {
      session: null,
    };

    this.telemetryLoggingEnabled = this.isTelemetryLoggingEnabled();
    if (this.telemetryLoggingEnabled) {
      console.info(
        "[Fuel Calculator][Telemetry] Logging enabled via URL param (?log_telemetry=1 or ?log_telemery=1)",
      );
    }

    this.cacheElements();
  this.buildStepControls();
    this.setupCustomSelects();
    this.bindHandlers();
    this.bindExternalEventListeners();

    this.classList.remove("fuel-calculator--open");
    this.modal.hidden = true;
    this.backdrop.hidden = true;
    if (this.launcher) this.launcher.setAttribute("aria-expanded", "false");
    document.body.classList.remove("fuel-calculator-modal-open");
    this.applyHideSelectors();

    this.initializeStepUi();
  }

  disconnectedCallback() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    if (this.launcher)
      this.launcher.removeEventListener("click", this.boundOpenModal);
    if (this.closeBtn)
      this.closeBtn.removeEventListener("click", this.boundCloseModal);
    if (this.backBtn)
      this.backBtn.removeEventListener("click", this.boundBackStep);
    if (this.backdrop)
      this.backdrop.removeEventListener("click", this.boundCloseModal);
    if (this.stepsRoot)
      this.stepsRoot.removeEventListener("change", this.boundStepChange);
    if (this.productsList)
      this.productsList.removeEventListener("click", this.boundProductAddClick);
    if (this.selectedValuesWrap)
      this.selectedValuesWrap.removeEventListener(
        "click",
        this.boundSelectedStepClick,
      );
    if (this.retryBtn)
      this.retryBtn.removeEventListener("click", this.boundResetAll);
    if (this.addToCartBtn)
      this.addToCartBtn.removeEventListener("click", this.boundAddToCart);
    if (this.customSelectsTeardown) this.customSelectsTeardown();
    document.removeEventListener("click", this.boundDocumentClick);

    document.removeEventListener("keydown", this.boundEscapeHandler);
    window.removeEventListener("resize", this.boundResizeHandler);
    window.visualViewport?.removeEventListener(
      "resize",
      this.boundViewportResizeHandler,
    );
    window.visualViewport?.removeEventListener(
      "scroll",
      this.boundViewportResizeHandler,
    );
  }

  parseConfig() {
    const configEl = this.querySelector("[data-fuel-config]");
    if (!configEl) return {};

    try {
      return JSON.parse(configEl.textContent || "{}");
    } catch (error) {
      console.error("Fuel calculator config parse failed", error);
      return {};
    }
  }

  /**
   * @param {unknown} rawSteps
   * @returns {FuelStepDefinition[]}
   */
  normalizeStepDefinitions(rawSteps) {
    const parsedRawSteps = this.parsePotentialJson(rawSteps);
    const candidateSteps = this.extractStepsArray(parsedRawSteps);

    return this.ensureArray(candidateSteps)
      .map((step) => {
        const key = String(step?.key || "").trim();
        const label = String(step?.label || "").trim();
        if (!key || !label) return null;

        return {
          key,
          label,
          options: this.ensureArray(step?.options)
            .map((option) => {
              const optionValue = String(option?.value ?? "").trim();
              const optionKey = String(option?.key ?? "").trim();
              const optionKeyEn = String(option?.key_en ?? "").trim();

              if (!optionKey || !optionKeyEn || !optionValue) return null;

              return {
                key: optionKey,
                key_en: optionKeyEn,
                value: optionValue,
                label: optionValue,
                parentPath: this.ensureArray(option?.parentPath).map(
                  (pathEntry) => ({
                    step: String(pathEntry?.step || "").trim(),
                    value: String(pathEntry?.value ?? "").trim(),
                  }),
                ),
              };
            })
            .filter(Boolean),
        };
      })
      .filter(Boolean);
  }

  parsePotentialJson(value) {
    if (typeof value !== "string") return value;

    const trimmed = value.trim();
    if (!trimmed) return value;

    if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) {
      return value;
    }

    try {
      return JSON.parse(trimmed);
    } catch (_error) {
      return value;
    }
  }

  extractStepsArray(value) {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== "object") return [];

    if (Array.isArray(value.steps)) return value.steps;
    if (Array.isArray(value.data?.steps)) return value.data.steps;
    if (Array.isArray(value.value?.steps)) return value.value.steps;

    return [];
  }

  cacheElements() {
    this.launcher = this.querySelector("[data-fuel-launcher]");
    this.backdrop = this.querySelector("[data-fuel-backdrop]");
    this.modal = this.querySelector("[data-fuel-modal]");
    this.content = this.querySelector("[data-fuel-content]");
    this.closeBtn = this.querySelector("[data-fuel-close]");
    this.backBtn = this.querySelector("[data-fuel-back]");

    this.stepsRoot = this.querySelector("[data-fuel-steps]");
    this.resultStep = this.querySelector('[data-step="result"]');

    const legacySportSelect = this.querySelector("[data-fuel-sport]");
    const legacyThresholdSelect = this.querySelector("[data-fuel-threshold]");
    const legacySessionSelect = this.querySelector("[data-fuel-session]");

    this.legacyStepPlaceholders = {
      sport: legacySportSelect?.options?.[0]?.textContent || "",
      threshold: legacyThresholdSelect?.options?.[0]?.textContent || "",
      session: legacySessionSelect?.options?.[0]?.textContent || "",
    };

    this.selectedValuesWrap = this.querySelector("[data-fuel-selected-values]");
    this.selectedValuesTitle = this.querySelector(
      ".fuel-calculator__selected-values-title",
    );

    Array.from(
      this.selectedValuesWrap?.querySelectorAll("[data-fuel-selected-sport], [data-fuel-selected-threshold], [data-fuel-selected-session]") ||
        [],
    ).forEach((node) => node.remove());

    this.footerHint = this.querySelector("[data-fuel-footer-hint]");

    this.metricsWrap = this.querySelector("[data-fuel-metrics]");
    this.planWrap = this.querySelector("[data-fuel-plan]");
    this.planGrid = this.querySelector("[data-fuel-plan-grid]");
    this.totalsWrap = this.querySelector("[data-fuel-totals]");
    this.summaryWrap = this.querySelector("[data-fuel-summary]");
    this.productsWrap = this.querySelector("[data-fuel-products]");
    this.productsList = this.querySelector("[data-fuel-products-list]");

    this.retryBtn = this.querySelector("[data-fuel-retry]");
    this.addToCartBtn = this.querySelector("[data-fuel-add-to-cart]");
  }

  buildStepControls() {
    this.stepControls = [];
    if (!this.stepsRoot || !this.stepDefinitions.length) return;

    const staticStepNodes = Array.from(
      this.stepsRoot.querySelectorAll('.fuel-calculator__step[data-step]'),
    ).filter((node) => node.dataset.step !== "result");
    staticStepNodes.forEach((node) => node.remove());

    const insertionPoint = this.footerHint || this.resultStep || null;
    const sectionId = String(this.dataset.sectionId || "fuel");

    this.stepDefinitions.forEach((step, index) => {
      const container = document.createElement("div");
      container.className = "fuel-calculator__step";
      container.dataset.step = step.key;
      container.hidden = index !== 0;

      const label = document.createElement("label");
      label.className = "fuel-calculator__label";
      label.textContent = step.label;

      const selectWrap = document.createElement("div");
      selectWrap.className = "fuel-calculator__select-wrap";

      const select = document.createElement("select");
      const selectId = `FuelStep-${sectionId}-${index}`;
      select.id = selectId;
      select.className = "fuel-calculator__select";
      select.dataset.fuelStepIndex = String(index);

      const placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.textContent = this.getStepPlaceholder(step, index);
      select.appendChild(placeholder);

      label.setAttribute("for", selectId);
      selectWrap.appendChild(select);
      container.appendChild(label);
      container.appendChild(selectWrap);

      if (insertionPoint) {
        this.stepsRoot.insertBefore(container, insertionPoint);
      } else {
        this.stepsRoot.appendChild(container);
      }

      this.stepControls.push({
        container,
        label,
        select,
      });
    });
  }

  getStepPlaceholder(step, index) {
    const normalizedKey = String(step?.key || "").trim().toLowerCase();

    if (normalizedKey === "sport" && this.legacyStepPlaceholders.sport) {
      return this.legacyStepPlaceholders.sport;
    }

    if (
      ["threshold", "threshholdpower"].includes(normalizedKey) &&
      this.legacyStepPlaceholders.threshold
    ) {
      return this.legacyStepPlaceholders.threshold;
    }

    if (normalizedKey === "session" && this.legacyStepPlaceholders.session) {
      return this.legacyStepPlaceholders.session;
    }

    return `Select ${step?.label || `Step ${index + 1}`}`;
  }

  bindHandlers() {
    this.boundOpenModal = this.openModal.bind(this);
    this.boundCloseModal = this.closeModal.bind(this);
    this.boundBackStep = this.handleBackStep.bind(this);
    this.boundStepChange = (event) => {
      const select = event.target.closest("[data-fuel-step-index]");
      if (!select || !this.stepsRoot?.contains(select)) return;

      const index = Number(select.dataset.fuelStepIndex);
      if (!Number.isInteger(index) || index < 0) return;

      this.onStepChange(index);
    };
    this.boundProductAddClick = this.onProductAddClick.bind(this);
    this.boundSelectedStepClick = this.onSelectedStepClick.bind(this);
    this.boundResetAll = this.resetAll.bind(this);
    this.boundAddToCart = this.addSelectedItemsToCart.bind(this);
    this.boundResizeHandler = this.handleWindowResize.bind(this);
    this.boundViewportResizeHandler = this.updateViewportHeight.bind(this);
    this.boundDocumentClick = this.onDocumentClick.bind(this);
    this.boundEscapeHandler = (event) => {
      if (event.key === "Escape" && this.modal && !this.modal.hidden) {
        this.closeAllCustomSelects();
        this.closeModal();
      }
    };

    if (this.launcher)
      this.launcher.addEventListener("click", this.boundOpenModal);
    if (this.closeBtn)
      this.closeBtn.addEventListener("click", this.boundCloseModal);
    if (this.backBtn)
      this.backBtn.addEventListener("click", this.boundBackStep);
    if (this.backdrop)
      this.backdrop.addEventListener("click", this.boundCloseModal);
    if (this.stepsRoot)
      this.stepsRoot.addEventListener("change", this.boundStepChange);
    if (this.productsList)
      this.productsList.addEventListener("click", this.boundProductAddClick);
    if (this.selectedValuesWrap)
      this.selectedValuesWrap.addEventListener(
        "click",
        this.boundSelectedStepClick,
      );
    if (this.retryBtn)
      this.retryBtn.addEventListener("click", this.boundResetAll);
    if (this.addToCartBtn)
      this.addToCartBtn.addEventListener("click", this.boundAddToCart);

    document.addEventListener("click", this.boundDocumentClick);
    document.addEventListener("keydown", this.boundEscapeHandler);
    window.addEventListener("resize", this.boundResizeHandler);
    window.visualViewport?.addEventListener(
      "resize",
      this.boundViewportResizeHandler,
    );
    window.visualViewport?.addEventListener(
      "scroll",
      this.boundViewportResizeHandler,
    );
  }

  bindExternalEventListeners() {
    document.addEventListener("menu:open", () => this.closeModal());
    document.addEventListener("search:open", () => this.closeModal());
    document.addEventListener("fuelcalc:do-open", () => this.openModal());
  }

  handleWindowResize() {
    this.updateSelectedValues();
    this.updateViewportHeight();
  }

  updateViewportHeight() {
    const viewportHeight =
      window.visualViewport?.height || window.innerHeight || 0;

    if (!viewportHeight) return;

    this.style.setProperty(
      "--fuel-calculator-viewport-height",
      `${Math.round(viewportHeight)}px`,
    );
  }

  setupCustomSelects() {
    const selects = this.ensureArray(this.stepControls)
      .map((control) => control.select)
      .filter(Boolean);

    this.customSelects = new Map();
    this.customSelectTriggers = [];

    const teardownCallbacks = selects.map((select) =>
      this.setupCustomSelect(select),
    );

    this.customSelectsTeardown = () => {
      teardownCallbacks.forEach((teardown) => {
        if (typeof teardown === "function") teardown();
      });
      this.customSelects.clear();
      this.customSelectTriggers = [];
    };
  }

  setupCustomSelect(select) {
    const wrapper = select.closest(".fuel-calculator__select-wrap");
    if (!wrapper) return null;

    wrapper.classList.add("fuel-calculator__select-wrap--custom");

    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "fuel-calculator__custom-select-trigger";
    trigger.setAttribute("aria-haspopup", "listbox");
    trigger.setAttribute("aria-expanded", "false");

    const value = document.createElement("span");
    value.className = "fuel-calculator__custom-select-value";

    const caret = document.createElement("span");
    caret.className = "fuel-calculator__custom-select-caret";
    caret.setAttribute("aria-hidden", "true");

    trigger.appendChild(value);
    trigger.appendChild(caret);

    const menu = document.createElement("div");
    menu.className = "fuel-calculator__custom-select-menu";
    menu.setAttribute("role", "listbox");
    menu.hidden = true;

    wrapper.appendChild(trigger);
    wrapper.appendChild(menu);

    const state = {
      wrapper,
      select,
      trigger,
      menu,
      value,
    };

    this.customSelects.set(select, state);
    this.customSelectTriggers.push(trigger);

    const onTriggerClick = () => {
      if (menu.hidden) {
        this.openCustomSelect(select);
      } else {
        this.closeCustomSelect(select);
      }
    };

    const onMenuClick = (event) => {
      const optionBtn = event.target.closest("[data-fuel-option-value]");
      if (!optionBtn || !menu.contains(optionBtn)) return;

      const nextValue = String(optionBtn.dataset.fuelOptionValue || "");
      select.value = nextValue;
      select.dispatchEvent(new Event("change", { bubbles: true }));
      this.closeCustomSelect(select);
    };

    const onSelectChange = () => {
      this.syncCustomSelectValue(select);
      this.renderCustomSelectOptions(select);
    };

    trigger.addEventListener("click", onTriggerClick);
    menu.addEventListener("click", onMenuClick);
    select.addEventListener("change", onSelectChange);

    this.syncCustomSelectValue(select);
    this.renderCustomSelectOptions(select);

    return () => {
      trigger.removeEventListener("click", onTriggerClick);
      menu.removeEventListener("click", onMenuClick);
      select.removeEventListener("change", onSelectChange);

      trigger.remove();
      menu.remove();
      wrapper.classList.remove("fuel-calculator__select-wrap--custom");
    };
  }

  onDocumentClick(event) {
    if (!this.customSelects || !this.customSelects.size) return;
    const clickedInside = event.target.closest(".fuel-calculator__select-wrap");
    if (clickedInside) return;
    this.closeAllCustomSelects();
  }

  openCustomSelect(select) {
    const state = this.customSelects?.get(select);
    if (!state) return;

    this.closeAllCustomSelects(select);
    state.wrapper.classList.add("is-open");
    state.menu.hidden = false;
    state.trigger.setAttribute("aria-expanded", "true");
    this.updateDropdownOpenState();
  }

  closeCustomSelect(select) {
    const state = this.customSelects?.get(select);
    if (!state) return;

    state.wrapper.classList.remove("is-open");
    state.menu.hidden = true;
    state.trigger.setAttribute("aria-expanded", "false");
    this.updateDropdownOpenState();
  }

  closeAllCustomSelects(exceptSelect = null) {
    if (!this.customSelects || !this.customSelects.size) return;

    this.customSelects.forEach((state, select) => {
      if (exceptSelect && select === exceptSelect) return;
      state.wrapper.classList.remove("is-open");
      state.menu.hidden = true;
      state.trigger.setAttribute("aria-expanded", "false");
    });

    this.updateDropdownOpenState();
  }

  updateDropdownOpenState() {
    if (!this.customSelects || !this.customSelects.size) {
      this.classList.remove("fuel-calculator--dropdown-open");
      return;
    }

    const hasOpenDropdown = Array.from(this.customSelects.values()).some(
      (state) => !state.menu.hidden,
    );

    this.classList.toggle("fuel-calculator--dropdown-open", hasOpenDropdown);
  }

  syncCustomSelectValue(select) {
    const state = this.customSelects?.get(select);
    if (!state) return;

    const selectedOption =
      select.options[select.selectedIndex] || select.options[0] || null;
    state.value.textContent = selectedOption ? selectedOption.textContent : "";
  }

  renderCustomSelectOptions(select) {
    const state = this.customSelects?.get(select);
    if (!state) return;

    state.menu.innerHTML = "";

    Array.from(select.options).forEach((option) => {
      if (!String(option.value || "").trim()) return;

      const optionBtn = document.createElement("button");
      optionBtn.type = "button";
      optionBtn.className = "fuel-calculator__custom-select-option";
      optionBtn.dataset.fuelOptionValue = option.value;
      optionBtn.setAttribute("role", "option");

      if (option.selected) {
        optionBtn.classList.add("is-selected");
        optionBtn.setAttribute("aria-selected", "true");
      }

      optionBtn.innerHTML = `<span class="fuel-calculator__custom-select-text">${option.textContent}</span>`;
      state.menu.appendChild(optionBtn);
    });
  }

  ensureArray(value) {
    if (!value) return [];
    return Array.isArray(value)
      ? value.filter(Boolean)
      : [value].filter(Boolean);
  }

  isTelemetryLoggingEnabled() {
    try {
      const params = new URLSearchParams(window.location.search || "");
      const rawValue = params.get("log_telemetry");
      if (rawValue === null) return false;

      const normalizedValue = String(rawValue).trim().toLowerCase();
      if (!normalizedValue) return true;

      return ["1", "true", "yes", "on", "debug"].includes(normalizedValue);
    } catch (_error) {
      return false;
    }
  }

  logTelemetry(eventName, payload = {}) {
    if (!this.telemetryLoggingEnabled) return;

    console.info("[Fuel Calculator][Telemetry Event]", {
      event: eventName,
      payload,
      path: window.location.pathname,
    });
  }

  publishFuelEvent(eventName, payload = {}) {
    this.logTelemetry(eventName, payload);

    this.publishFuelEventDefault(eventName, payload);
    this.publishFuelEventGa(eventName, payload);
    this.publishFuelEventGlobal(eventName, payload);
  }

  publishFuelEventDefault(eventName, payload = {}) {
    const publishEvent = window.Shopify?.analytics?.publish;
    if (typeof publishEvent !== "function") return;

    try {
      publishEvent(eventName, payload);
    } catch (error) {
      console.error("Fuel calculator analytics publish failed", error);
    }
  }

  publishFuelEventGa(eventName, payload = {}) {
    if (typeof window.gtag !== "function") return;
    try {
      const prefixedPayload = {};
      for (const key in payload) {
        prefixedPayload[key.startsWith("fc_") ? key : `fc_${key}`] =
          payload[key];
      }
      this.logTelemetry("ga4: " + eventName, prefixedPayload);
      window.gtag("event", eventName, prefixedPayload);
    } catch (error) {
      console.error("Fuel calculator ga publish failed", error);
    }
  }

  publishFuelEventGlobal(eventName, payload = {}) {
    try {
      payload.action = eventName;
      document.dispatchEvent(
        new CustomEvent("fuel_calculator", { bubbles: true, detail: payload }),
      );
    } catch (error) {
      console.error("Fuel calculator global analytics event failed", error);
    }
  }

  applyHideSelectors() {
    const hideSelectors = String(this.config.hideSelectors || "")
      .split(/[,\n]/)
      .map((selector) => selector.trim())
      .filter(Boolean);

    hideSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((node) => {
        node.classList.add("fuel-calculator-hide-when-modal-open");
      });
    });
  }

  initializeStepUi() {
    this.updateViewportHeight();

    if (!this.stepDefinitions.length) {
      this.showStep({ threshold: false, session: false, result: false });
      console.warn(
        "Fuel calculator requires config.steps with at least one step definition.",
      );
      return;
    }

    this.stepIndexByKey = this.stepDefinitions.reduce((accumulator, step, index) => {
      accumulator[step.key] = index;
      return accumulator;
    }, {});
    this.maxVisibleStepCount = this.countRemainingReachableSteps(0);

    for (let index = 0; index < this.stepDefinitions.length; index += 1) {
      this.setStepLabel(index);
    }

    this.loadStepOptions(0);
    this.resetResult();
    this.showStepByIndex(0);
  }

  getStepControl(index) {
    return this.stepControls[index] || null;
  }

  getStepDefinition(index) {
    return this.stepDefinitions[index] || null;
  }

  getStepSelectionByIndex(index) {
    const step = this.getStepDefinition(index);
    if (!step) return null;
    return this.stepSelections[step.key];
  }

  getStepIndexFromJumpTarget(targetStep) {
    const normalizedTarget = String(targetStep || "").trim();
    if (!normalizedTarget) return -1;

    const aliases = {
      sport: 0,
      threshold: 1,
      session: 2,
    };

    if (Object.prototype.hasOwnProperty.call(aliases, normalizedTarget)) {
      return aliases[normalizedTarget];
    }

    return this.stepDefinitions.findIndex(
      (step) => step.key === normalizedTarget,
    );
  }

  setStepLabel(index) {
    const step = this.getStepDefinition(index);
    const control = this.getStepControl(index);
    if (!step || !control?.container) return;

    const label =
      control.label || control.container.querySelector(".fuel-calculator__label");
    if (!label) return;
    label.textContent = step.label;
  }

  updateSelectedValues() {
    if (!this.selectedValuesWrap) return;

    if (this.isResultStepActive) {
      this.selectedValuesWrap.hidden = true;
      return;
    }

    Array.from(
      this.selectedValuesWrap.querySelectorAll("[data-fuel-generated-pill]"),
    ).forEach((node) => node.remove());

    const selectedEntries = this.stepDefinitions
      .map((step, index) => {
        const selected = this.getStepSelectionByIndex(index);
        if (!selected?.label) return null;

        return {
          stepKey: step.key,
          label: selected.label,
        };
      })
      .filter(Boolean);

    const hasAnyValue = selectedEntries.length > 0;

    this.selectedValuesWrap.hidden = !hasAnyValue;
    if (!hasAnyValue) return;

    const pillsFragment = document.createDocumentFragment();

    selectedEntries.forEach((entry) => {
      const pill = document.createElement("button");
      pill.type = "button";
      pill.className = "fuel-calculator__selected-pill";
      pill.dataset.fuelJumpStep = entry.stepKey;
      pill.dataset.fuelGeneratedPill = "true";
      pill.textContent = String(entry.label).toUpperCase();
      pillsFragment.appendChild(pill);
    });

    if (this.selectedValuesTitle && this.selectedValuesTitle.parentNode) {
      this.selectedValuesTitle.parentNode.insertBefore(
        pillsFragment,
        this.selectedValuesTitle.nextSibling,
      );
      return;
    }

    this.selectedValuesWrap.prepend(pillsFragment);
  }

  toDatasetHintKey(stepKey) {
    const normalized = String(stepKey || "").trim();
    if (!normalized) return "";

    const lowerCased = normalized.charAt(0).toLowerCase() + normalized.slice(1);
    return `hint${lowerCased.replace(/[-_\s]+([a-zA-Z0-9])/g, (_match, char) =>
      char.toUpperCase(),
    )}`;
  }

  matchesParentPathWithCurrentSelections(parentPath) {
    return this.ensureArray(parentPath).every((pathEntry) => {
      const selected = this.stepSelections[pathEntry.step];
      if (!selected) return true;
      return String(selected.value) === String(pathEntry.value);
    });
  }

  isOptionCompatibleWithSelections(option, currentStepIndex, selectionsMap) {
    const optionParentPath = this.ensureArray(option?.parentPath);
    if (!optionParentPath.length) return true;

    return optionParentPath.every((pathEntry) => {
      const parentStepKey = String(pathEntry?.step || "").trim();
      const parentStepValue = String(pathEntry?.value ?? "").trim();
      if (!parentStepKey || !parentStepValue) return false;

      const parentStepIndex = this.stepIndexByKey?.[parentStepKey];
      if (!Number.isInteger(parentStepIndex)) return false;
      if (parentStepIndex >= currentStepIndex) return false;

      const selected = selectionsMap[parentStepKey];
      if (!selected) return false;

      return String(selected.value) === parentStepValue;
    });
  }

  countMaxReachableStepsFrom(index, selectionsMap) {
    if (index >= this.stepDefinitions.length) return 0;

    const step = this.getStepDefinition(index);
    if (!step) return this.countMaxReachableStepsFrom(index + 1, selectionsMap);

    const options = this.ensureArray(step.options).filter((option) =>
      this.isOptionCompatibleWithSelections(option, index, selectionsMap),
    );

    if (!options.length) {
      return this.countMaxReachableStepsFrom(index + 1, selectionsMap);
    }

    const existingSelection = selectionsMap[step.key];
    if (existingSelection) {
      const matchedOption = options.find(
        (option) => String(option.value) === String(existingSelection.value),
      );

      if (!matchedOption) {
        return this.countMaxReachableStepsFrom(index + 1, selectionsMap);
      }

      return 1 + this.countMaxReachableStepsFrom(index + 1, selectionsMap);
    }

    let bestPathCount = 0;

    options.forEach((option) => {
      const nextSelections = {
        ...selectionsMap,
        [step.key]: {
          stepKey: step.key,
          key: option.key,
          key_en: option.key_en,
          value: option.value,
          label: option.label,
        },
      };

      const pathCount = 1 + this.countMaxReachableStepsFrom(index + 1, nextSelections);
      if (pathCount > bestPathCount) bestPathCount = pathCount;
    });

    return bestPathCount;
  }

  hasPotentialOptions(stepDefinition) {
    if (!stepDefinition) return false;

    return this.ensureArray(stepDefinition.options).some((option) =>
      this.matchesParentPathWithCurrentSelections(option.parentPath),
    );
  }

  countRemainingReachableSteps(fromIndex) {
    const boundedIndex = Math.max(0, Number(fromIndex) || 0);
    return this.countMaxReachableStepsFrom(boundedIndex, {
      ...this.stepSelections,
    });
  }

  getAvailableStepOptions(stepDefinition) {
    if (!stepDefinition) return [];

    return this.ensureArray(stepDefinition.options).filter((option) => {
      const parentPath = this.ensureArray(option.parentPath);
      if (!parentPath.length) return true;

      return parentPath.every((pathEntry) => {
        const selected = this.stepSelections[pathEntry.step];
        return selected && String(selected.value) === String(pathEntry.value);
      });
    });
  }

  setValueOptions(select, entries, placeholderText) {
    if (!select) return;
    select.innerHTML = "";

    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = placeholderText;
    select.appendChild(placeholder);

    entries.forEach((entry) => {
      if (!entry || !entry.value) return;
      const option = document.createElement("option");
      option.value = entry.value;
      option.textContent = entry.value;
      select.appendChild(option);
    });

    select.value = "";
    this.syncCustomSelectValue(select);
    this.renderCustomSelectOptions(select);
  }

  loadStepOptions(index) {
    const step = this.getStepDefinition(index);
    const control = this.getStepControl(index);
    if (!step || !control?.select) return;

    this.setValueOptions(
      control.select,
      this.getAvailableStepOptions(step),
      control.select.options[0]?.textContent || `Select ${step.label}`,
    );
  }

  findNextStepWithOptions(fromIndex) {
    for (
      let index = fromIndex;
      index < this.stepDefinitions.length;
      index += 1
    ) {
      const step = this.getStepDefinition(index);
      const control = this.getStepControl(index);
      if (!step || !control?.select) continue;

      const options = this.getAvailableStepOptions(step);
      if (options.length > 0) {
        return index;
      }
    }

    return -1;
  }

  clearSelectionsFrom(startIndex) {
    for (
      let index = startIndex;
      index < this.stepDefinitions.length;
      index += 1
    ) {
      const step = this.getStepDefinition(index);
      if (!step) continue;

      this.stepSelections[step.key] = null;

      const control = this.getStepControl(index);
      if (control?.select) {
        control.select.value = "";
        this.syncCustomSelectValue(control.select);
        this.renderCustomSelectOptions(control.select);
      }
    }

    this.state.session = null;
  }

  updateFooterHint(stepKey = "sport") {
    if (!this.footerHint) return;

    if (stepKey === "result") {
      this.footerHint.textContent = "";
      this.footerHint.hidden = true;
      return;
    }

    const targetIndex = this.getStepIndexFromJumpTarget(stepKey);
    const activeIndex = targetIndex >= 0 ? targetIndex : this.getCurrentStepIndex();

    const remainingSteps =
      this.countRemainingReachableSteps(activeIndex) ||
      this.maxVisibleStepCount ||
      this.stepDefinitions.length;

    const hintTemplate =
      String(this.footerHint.dataset.stepsLeftTemplate || "").trim();

    let nextHint = hintTemplate;
    if (nextHint.includes("{steps_left}")) {
      nextHint = nextHint.replaceAll("{steps_left}", String(remainingSteps));
    }

    this.footerHint.textContent = nextHint;
    this.footerHint.hidden = !nextHint;
  }

  showResultStep() {
    this.isResultStepActive = true;
    this.closeAllCustomSelects();

    this.stepControls.forEach((control) => {
      if (control?.container) control.container.hidden = true;
    });

    if (this.resultStep) this.resultStep.hidden = false;
    this.activeStepIndex = Math.max(this.stepDefinitions.length - 1, 0);
    this.updateFooterHint("result");
    this.updateBackButtonVisibility("result");
    this.updateSelectedValues();
  }

  showStepByIndex(index) {
    if (!this.stepDefinitions.length) return;

    const boundedIndex = Math.min(
      Math.max(index, 0),
      this.stepDefinitions.length - 1,
    );

    this.isResultStepActive = false;
    this.activeStepIndex = boundedIndex;
    this.closeAllCustomSelects();

    this.stepControls.forEach((control, controlIndex) => {
      if (!control?.container) return;
      control.container.hidden = controlIndex !== boundedIndex;
    });

    if (this.resultStep) this.resultStep.hidden = true;

    const activeStep = this.getStepDefinition(boundedIndex)?.key || "sport";
    this.updateFooterHint(activeStep);
    this.updateBackButtonVisibility(activeStep);
    this.updateSelectedValues();
  }

  updateBackButtonVisibility(activeStep) {
    if (!this.backBtn) return;
    const shouldShow =
      activeStep === "result" || (Number(this.activeStepIndex) || 0) > 0;
    this.backBtn.hidden = !shouldShow;
    this.backBtn.disabled = !shouldShow;
  }

  normalizeLocaleValue(locale) {
    const normalized = String(locale || "")
      .trim()
      .toLowerCase();
    if (!normalized) return "";

    return normalized.split("-")[0];
  }

  isPrimaryLocale() {
    return String(this.dataset.isPrimaryLocale || "").trim() === "true";
  }

  getResolvedLocale() {
    const fromComponent = this.normalizeLocaleValue(this.dataset.locale);
    if (!fromComponent) {
      throw new Error(
        "Missing required data-locale on fuel calculator component",
      );
    }

    return fromComponent;
  }

  buildLocalizedProductUrl(handle) {
    const locale = this.getResolvedLocale();

    if (this.isPrimaryLocale()) {
      return `/products/${handle}.js`;
    }

    return `/${locale}/products/${handle}.js`;
  }

  getProxyPayload() {
    const identifier = this.stepDefinitions
      .map((step) => this.stepSelections[step.key])
      .filter(Boolean)
      .map((selected) => {
        if (!selected.key) {
          throw new Error("Missing selected key in fuel calculator selection");
        }

        return selected.key;
      })
      .join("")
      .replace(/\s+/g, "");

    if (!identifier) {
      throw new Error("Missing identifier for fuel calculator proxy payload");
    }

    const locale = this.getResolvedLocale();

    return {
      identifier,
      locale,
    };
  }

  async fetchProxyResult() {
    const endpoint = String(this.config.proxyEndpoint || "").trim();
    if (!endpoint) {
      throw new Error("Missing proxy endpoint in fuel calculator config");
    }

    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(this.getProxyPayload()),
    });

    if (!response.ok) {
      throw new Error(
        `Fuel calculator proxy failed with status ${response.status}`,
      );
    }

    return response.json();
  }

  async resolveStepSelectionResult() {
    const basePayload = this.getProxyPayload();

    this.publishFuelEvent("fuel_calculator_resolve", {
      mode: "proxy",
      ...basePayload,
      status: "clicked",
    });

    try {
      const proxyResponse = await this.fetchProxyResult();
      const sessionLike = this.normalizeProxySessionPayload(proxyResponse);

      if (!sessionLike || typeof sessionLike !== "object") {
        throw new Error("Fuel calculator proxy returned an invalid payload");
      }
      // console.log("Resolved fuel calculator session via proxy:", sessionLike);
      await this.enrichSessionProducts(sessionLike);

      this.state.session = sessionLike;
      this.renderSessionResult(sessionLike);
    } catch (error) {
      console.error(
        "Failed to resolve fuel calculator session via proxy",
        error,
      );
    }
  }

  normalizeProxySessionPayload(proxyResponse) {
    const source =
      proxyResponse?.data && typeof proxyResponse.data === "object"
        ? proxyResponse.data
        : proxyResponse?.session || proxyResponse?.result || proxyResponse;

    if (!source || typeof source !== "object") return null;

    const normalized = {
      carbsperhour: source.carbsPerHour,
      sodium: source.sodium,
      fluid: source.fluid,
      fuelplan: source.fuelPlan,
      totalintake: source.totalIntake,
      variants: this.ensureArray(source.variants),
      fallbackvariants: this.ensureArray(source.fallback_variants),
    };

    const fuelPlanRows = this.ensureArray(source.fuelPlan)
      .join(",")
      .split(",")
      .map((entry) => String(entry || "").trim())
      .filter(Boolean);

    if (fuelPlanRows.length) {
      const times = [];
      const labels = [];

      fuelPlanRows.forEach((row) => {
        // "<label>: <text>" — colon must be followed by whitespace, so ratios like "1:0.8" are ignored.
        const colonMatch = row.match(/^([^:#]+?):\s+(.+)$/);
        if (colonMatch) {
          times.push(colonMatch[1].trim());
          labels.push(colonMatch[2].trim());
          return;
        }

        const skuIndex = row.indexOf("#");
        if (skuIndex > 0) {
          times.push(row.slice(0, skuIndex).trim());
          labels.push(row.slice(skuIndex).trim());
          return;
        }

        const match = row.match(/^(\d+\s*h)\s+(.+)$/i);
        if (match) {
          times.push(match[1]);
          labels.push(match[2]);
          return;
        }

        // Keep times/labels aligned by index.
        times.push("");
        labels.push(row);
      });

      if (times.length) normalized.times = times;
      normalized.planlabels = labels;
    }

    return normalized;
  }

  async enrichSessionProducts(session) {
    const variants = this.ensureArray(session.variants);
    const fallbackVariants = this.ensureArray(session.fallbackvariants);

    if (!variants.length) {
      session.pairs = [];
      return;
    }

    const handles = [
      ...new Set(
        [...variants, ...fallbackVariants]
          .map((variant) => variant?.product_handle)
          .filter(Boolean),
      ),
    ];

    const productByHandle = {};

    const productResults = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(this.buildLocalizedProductUrl(handle));
          if (!response.ok) return [handle, null];
          const data = await response.json();
          return [handle, data];
        } catch (_error) {
          return [handle, null];
        }
      }),
    );

    productResults.forEach(([handle, data]) => {
      productByHandle[handle] = data;

      if (!data) {
        console.warn(
          "[Fuel Calculator][Product Fetch] Failed to fetch product data",
          {
            handle,
          },
        );
      }
    });

    const primaryByIndex = new Map();
    variants.forEach((variantRef) => {
      const mapKey = String(variantRef?.index || "").trim();
      if (!mapKey || primaryByIndex.has(mapKey)) return;
      primaryByIndex.set(mapKey, variantRef);
    });

    const fallbackByIndex = new Map();
    fallbackVariants.forEach((variantRef) => {
      const mapKey = String(variantRef?.index || "").trim();
      if (!mapKey || fallbackByIndex.has(mapKey)) return;
      fallbackByIndex.set(mapKey, variantRef);
    });

    const enrichVariantRef = (variantRef) => {
      if (!variantRef) return null;

      const productData = productByHandle[variantRef?.product_handle] || null;
      const productVariant = this.findVariantById(productData, variantRef?.id);
      const productFeaturedImage =
        productData?.featured_image ||
        this.ensureArray(productData?.images)[0] ||
        null;

      // Keep index entries even when variant payload cannot be fully resolved.
      if (!productVariant) {
        return {
          id: String(variantRef?.id || ""),
          sku: String(variantRef?.sku || ""),
          productHandle: String(variantRef?.product_handle || ""),
          variantId: Number(variantRef?.id),
          inStock: false,
          title: String(productData?.title || variantRef?.sku || "").trim(),
          subtitle: "",
          price: "",
          image: productFeaturedImage,
        };
      }

      return {
        id: String(variantRef?.id || ""),
        sku: String(variantRef?.sku || ""),
        productHandle: String(variantRef?.product_handle || ""),
        variantId: Number(variantRef?.id),
        inStock: this.isVariantInStock(productData, variantRef?.id),
        title: this.buildVariantTitle(productData, productVariant, variantRef),
        subtitle: this.getVariantSubtitle(productVariant),
        price: this.formatVariantPrice(productVariant),
        image: this.resolveVariantImage(productVariant, productData),
      };
    };

    const indexKeys = Array.from(primaryByIndex.keys());

    session.pairs = indexKeys
      .map((indexKey) => {
        const variant = enrichVariantRef(primaryByIndex.get(indexKey));
        const fallback = enrichVariantRef(fallbackByIndex.get(indexKey));

        return {
          index: indexKey,
          variant,
          fallback,
        };
      })
      .filter(Boolean);
  }

  isVariantInStock(productData, wantedVariantId) {
    if (!productData || !wantedVariantId) return false;

    const wantedVariant = this.findVariantById(productData, wantedVariantId);
    if (!wantedVariant) return false;

    if (typeof wantedVariant.available === "boolean") {
      return wantedVariant.available;
    }

    if (typeof wantedVariant.inventory_quantity === "number") {
      if (wantedVariant.inventory_quantity > 0) return true;
      if (wantedVariant.inventory_policy === "continue") return true;
      return false;
    }

    return false;
  }

  findVariantById(productData, variantId) {
    if (!productData || !variantId) return null;
    const normalizedTargetId = String(variantId);
    return this.ensureArray(productData.variants).find(
      (variant) => String(variant.id) === normalizedTargetId,
    );
  }

  buildVariantTitle(productData, variantData, variantRef) {
    const productTitle = String(productData?.title || "").trim();
    const variantTitle = String(variantData?.title || "").trim();
    const sku = String(variantRef?.sku || "").trim();

    if (
      productTitle &&
      variantTitle &&
      variantTitle.toLowerCase() !== "default title"
    ) {
      return `${productTitle}`;
    }

    if (productTitle) return productTitle;
    if (variantTitle) return variantTitle;
    return sku;
  }

  getVariantSubtitle(variantData) {
    const variantTitle = String(variantData?.title || "").trim();
    if (!variantTitle) return "";
    if (variantTitle.toLowerCase() === "default title") return "";
    return variantTitle;
  }

  formatVariantPrice(variantData) {
    if (
      !variantData ||
      variantData.price === undefined ||
      variantData.price === null
    )
      return "";

    const cents = Number(variantData.price);
    if (!Number.isFinite(cents)) return "";

    const currencyCode = window.Shopify?.currency?.active || "EUR";
    const locale = String(document.documentElement.lang || "de");

    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currencyCode,
      }).format(cents / 100);
    } catch (_error) {
      return `${(cents / 100).toFixed(2)} ${currencyCode}`;
    }
  }

  resolveVariantImage(variantData, productData) {
    const variantImage =
      variantData?.featured_image?.src ||
      variantData?.featured_media?.preview_image?.src ||
      variantData?.image?.src ||
      variantData?.image;

    if (variantImage) return variantImage;

    return (
      productData?.featured_image ||
      this.ensureArray(productData?.images)[0] ||
      null
    );
  }

  async onStepChange(index) {
    const step = this.getStepDefinition(index);
    const control = this.getStepControl(index);
    if (!step || !control?.select) return;

    this.resetResult();

    const selectedValue = String(control.select.value || "").trim();
    const selectedOption = this.getAvailableStepOptions(step).find(
      (option) => String(option.value) === selectedValue,
    );

    this.stepSelections[step.key] = selectedOption
      ? {
          stepKey: step.key,
          key: selectedOption.key,
          key_en: selectedOption.key_en,
          value: selectedOption.value,
          label: selectedOption.label,
        }
      : null;

    this.clearSelectionsFrom(index + 1);

    this.publishFuelEvent("fuel_calculator_step", {
      step: index + 1,
      action: selectedOption ? "selected" : "cleared",
      selection_step: selectedOption ? step.key : null,
      selection_key: selectedOption ? selectedOption.key_en : null,
      selection_key_local: selectedOption ? selectedOption.key : null,
      selection_value: selectedOption ? selectedOption.value : null,
      selection_label: selectedOption ? selectedOption.label : null,
    });

    if (!selectedOption) {
      this.showStepByIndex(index);
      return;
    }

    const nextIndex = this.findNextStepWithOptions(index + 1);

    if (nextIndex >= 0) {
      this.loadStepOptions(nextIndex);
      this.showStepByIndex(nextIndex);
      return;
    }

    await this.resolveStepSelectionResult();
  }

  getCurrentStepIndex() {
    if (!Number.isInteger(this.activeStepIndex)) return 0;
    return this.activeStepIndex;
  }

  findPreviousApplicableStepIndex(fromIndex) {
    for (let index = fromIndex - 1; index >= 0; index -= 1) {
      if (index === 0) return 0;

      const step = this.getStepDefinition(index);
      const control = this.getStepControl(index);
      if (!step || !control?.select) continue;

      const options = this.getAvailableStepOptions(step);
      if (options.length > 0) return index;
    }

    return 0;
  }

  handleBackStep() {
    if (this.isResultStepActive) {
      const fallbackIndex = this.stepDefinitions.length - 1;
      if (fallbackIndex < 0) {
        this.showStepByIndex(0);
        return;
      }

      this.clearSelectionsFrom(fallbackIndex);
      this.loadStepOptions(fallbackIndex);
      this.showStepByIndex(fallbackIndex);
      return;
    }

    const currentIndex = this.getCurrentStepIndex();
    const targetIndex = this.findPreviousApplicableStepIndex(currentIndex);

    this.clearSelectionsFrom(targetIndex);
    this.loadStepOptions(targetIndex);
    this.showStepByIndex(targetIndex);
  }

  onSelectedStepClick(event) {
    const control = event.target.closest("[data-fuel-jump-step]");
    if (!control || !this.selectedValuesWrap?.contains(control)) return;

    const targetIndex = this.getStepIndexFromJumpTarget(
      control.dataset.fuelJumpStep,
    );
    if (targetIndex < 0 || targetIndex >= this.stepDefinitions.length) return;

    this.clearSelectionsFrom(targetIndex);

    for (
      let index = targetIndex;
      index < this.stepDefinitions.length;
      index += 1
    ) {
      this.loadStepOptions(index);
    }

    this.showStepByIndex(targetIndex);
  }

  resetResult() {
    this.metricsWrap.innerHTML = "";
    this.planGrid.innerHTML = "";
    this.totalsWrap.innerHTML = "";
    this.summaryWrap.innerHTML = "";
    this.summaryWrap.hidden = true;
    this.productsList.innerHTML = "";
    this.productsWrap.hidden = true;
    this.planWrap.hidden = true;
  }

  closeModal() {
    if (this.modal.hidden) return;
    this.closeAllCustomSelects();
    this.classList.remove("fuel-calculator--open");
    if (this.launcher) this.launcher.setAttribute("aria-expanded", "false");
    document.body.classList.remove("fuel-calculator-modal-open");
    document.dispatchEvent(new CustomEvent("fuelcalc:close"));

    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      this.modal.hidden = true;
      this.backdrop.hidden = true;
      this.closeTimer = null;
    }, 260);
  }

  openModal() {
    if (!this.modal.hidden && this.classList.contains("fuel-calculator--open"))
      return;
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    if (!this.getStepSelectionByIndex(0)) {
      this.showStepByIndex(0);
    }

    this.modal.hidden = false;
    this.backdrop.hidden = false;
    this.updateViewportHeight();

    document.dispatchEvent(new CustomEvent("fuelcalc:open"));

    this.publishFuelEvent("fuel_calculator_start", {
      step: 1,
      state: "opened",
    });

    if (this.launcher) this.launcher.setAttribute("aria-expanded", "true");
    document.body.classList.add("fuel-calculator-modal-open");

    requestAnimationFrame(() => {
      this.classList.add("fuel-calculator--open");
    });

    setTimeout(() => this.content?.focus(), 0);
  }

  parseQty(value) {
    const fromText = String(value || "").match(/\d+/);
    return fromText ? Number(fromText[0]) : 1;
  }

  toTrackingValue(value) {
    const normalizedValue = String(value || "").trim();
    return normalizedValue || null;
  }

  normalizeId(value) {
    if (value === null || value === undefined) return "";

    if (typeof value === "object") {
      if (value.system?.id) return this.normalizeId(value.system.id);
      if (value.id) return this.normalizeId(value.id);
      return "";
    }

    const stringValue = String(value).trim();
    if (!stringValue) return "";

    const gidMatch = stringValue.match(/gid:\/\/shopify\/Metaobject\/(\d+)/i);
    if (gidMatch && gidMatch[1]) return gidMatch[1];

    return stringValue;
  }

  getTrackingMeta(entity) {
    if (!entity) {
      return {
        key: null,
        value: null,
        local_key: null,
      };
    }

    const keyEn = this.toTrackingValue(entity.key_en);
    const keyLocal = this.toTrackingValue(entity.key);

    return {
      key: keyEn,
      value: this.toTrackingValue(entity.label),
      local_key: keyLocal,
    };
  }

  getSelectionEntity(index) {
    const selected = this.getStepSelectionByIndex(index);
    if (!selected) return null;
    return selected;
  }

  renderMetrics(session) {
    this.metricsWrap.innerHTML = "";

    const carbohydrates = session.carbsperhour;
    const sodium = session.sodium;
    const fluid = session.fluid;

    const metricMap = [
      {
        label: this.config.labels?.carbohydrates || "",
        value: carbohydrates,
      },
      { label: this.config.labels?.sodium || "", value: sodium },
      { label: this.config.labels?.fluid || "", value: fluid },
    ].filter((metric) => metric.value);

    metricMap.forEach((metric) => {
      const card = document.createElement("div");
      card.className = "fuel-calculator__metric";
      card.innerHTML = `<span class="fuel-calculator__metric-label">${metric.label}</span><span class="fuel-calculator__metric-value">${metric.value}</span>`;
      this.metricsWrap.appendChild(card);
    });
  }

  renderPlan(session) {
    const times = this.ensureArray(
      session.times || session.planTimes || session.fuel_plan_times,
    );
    const labels = this.ensureArray(session.planlabels);
    this.planGrid.innerHTML = "";

    if (!times.length && !labels.length) {
      this.planWrap.hidden = true;
      return;
    }

    const rows = Math.max(times.length, labels.length);

    for (let i = 0; i < rows; i += 1) {
      const row = document.createElement("div");
      row.className = "fuel-calculator__plan-row";

      const timeCell = document.createElement("span");
      timeCell.textContent = times[i] || "";

      const labelCell = document.createElement("span");
      labelCell.textContent = this.replacePlanSkus(labels[i] || "", session);

      row.appendChild(timeCell);
      row.appendChild(labelCell);
      this.planGrid.appendChild(row);
    }

    this.planWrap.hidden = false;
  }

  // Replaces "#SKU#" placeholders with the product title; uses the replacement
  // product's title when the primary product is unavailable.
  replacePlanSkus(label, session) {
    const titleBySku = new Map();
    this.ensureArray(session.pairs).forEach((pair) => {
      const primary = pair?.variant || null;
      const fallback = pair?.fallback || null;
      const preferred =
        (primary && primary.inStock && primary) ||
        (fallback && fallback.inStock && fallback) ||
        primary ||
        fallback;
      if (!preferred?.title) return;

      [primary?.sku, fallback?.sku].forEach((sku) => {
        const key = String(sku || "")
          .trim()
          .toLowerCase();
        if (key && !titleBySku.has(key)) titleBySku.set(key, preferred.title);
      });
    });

    return label
      .replace(/#([^#]+)#/g, (_match, sku) => {
        const trimmedSku = sku.trim();
        return titleBySku.get(trimmedSku.toLowerCase()) || trimmedSku;
      })
      .replace(/\s{2,}/g, " ")
      .trim();
  }

  renderTotals(session) {
    this.totalsWrap.innerHTML = "";
    this.totalsWrap.hidden = false;

    const totalIntake = session.totalintake;
    const totalLabel = this.config.labels?.totalLabel || "";

    const normalizeText = (value) => {
      const node = document.createElement("div");
      node.innerHTML = value || "";
      return (node.textContent || "").trim();
    };

    if (totalIntake) {
      const totalValue = normalizeText(totalIntake);
      if (totalValue) {
        this.totalsWrap.innerHTML = `<span class="fuel-calculator__totals-label">${totalLabel}</span><span class="fuel-calculator__totals-value">${totalValue}</span>`;
        return;
      }
    }

    this.totalsWrap.hidden = true;
  }

  getSessionPairs(session) {
    return this.ensureArray(session.pairs)
      .map((pair) => {
        const primary = pair?.variant || null;
        const fallback = pair?.fallback || null;

        const preferred =
          (primary && primary.inStock && primary) ||
          (fallback && fallback.inStock && fallback) ||
          primary ||
          fallback;

        if (!preferred) return null;

        const bothOutOfStock =
          Boolean(primary) &&
          Boolean(fallback) &&
          primary.inStock === false &&
          fallback.inStock === false;

        const image =
          bothOutOfStock && primary ? primary.image : preferred.image;

        return {
          variantId: preferred.variantId,
          title: preferred.title,
          subtitle: preferred.subtitle || "",
          price: preferred.price,
          image,
          inStock: preferred.inStock,
          productHandle: preferred.productHandle,
        };
      })
      .filter(Boolean);
  }

  renderProducts(session) {
    const variants = this.getSessionPairs(session);
    if (!variants.length) {
      this.productsWrap.hidden = true;
      return;
    }

    const addItemLabel = this.config.labels?.itemAddToCart || "ADD";
    this.productsList.innerHTML = "";

    variants.forEach((variant, index) => {
      const item = document.createElement("div");
      const hasVariantId = Number.isFinite(Number(variant.variantId));
      const canAdd = hasVariantId && variant.inStock !== false;

      const linkOpen = variant.productHandle ? `<a href="/products/${variant.productHandle}?variant=${variant.variantId}" target="_blank" title="${variant.title || ""}">` : '';
      const linkClose = linkOpen ? '</a>' : '';

      item.className = `fuel-calculator__product${canAdd ? "" : " is-unavailable"}`;

      const image = variant.image
        ? linkOpen + `<img src="${variant.image}" alt="${variant.title || ""}" class="fuel-calculator__product-image" loading="lazy">` + linkClose
        : '<div class="fuel-calculator__product-image"></div>';

      const qtyLabel = "";
      const subtitle = variant.subtitle && variant.subtitle.toLowerCase() !== 'default'
        ? `<span class="fuel-calculator__product-variant-title">${linkOpen}${variant.subtitle}${linkClose}</span>`
        : "";

      const itemAddButton = hasVariantId
        ? `<button type="button" class="fuel-calculator__product-add${canAdd ? "" : " fuel-calculator__product-add--disabled"}" data-fuel-product-add data-variant-id="${Number(variant.variantId)}" data-quantity="1" aria-label="${addItemLabel}"${canAdd ? "" : " disabled"}>${addItemLabel}</button>`
        : "";

      item.innerHTML = `${image}<div><span class="fuel-calculator__product-title">${linkOpen}${variant.title || ""}${linkClose}</span>${subtitle}${qtyLabel}${itemAddButton}</div><span class="fuel-calculator__product-price">${variant.price || ""}</span>`;

      item.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          this.publishFuelEvent('fuel_calculator_product_link', {
            product_title: variant.title || '',
            product_handle: variant.productHandle || '',
            variant_id: variant.variantId,
          });
        });
      });

      this.productsList.appendChild(item);
    });

    this.productsWrap.hidden = false;
  }

  renderSessionResult(session) {
    this.resetResult();
    this.renderMetrics(session);
    this.renderPlan(session);
    this.renderTotals(session);
    this.renderProducts(session);

    const firstTracking = this.getTrackingMeta(this.getSelectionEntity(0));
    const secondTracking = this.getTrackingMeta(this.getSelectionEntity(1));
    const sessionTracking = this.getTrackingMeta(this.getSelectionEntity(2));
    this.publishFuelEvent("fuel_calculator_complete", {
      sport_key: firstTracking.key,
      sport_key_local: firstTracking.local_key,
      threshold_key: secondTracking.key,
      threshold_key_local: secondTracking.local_key,
      session_key: sessionTracking.key,
      session_key_local: sessionTracking.local_key,
    });

    this.showResultStep();
  }

  getFuelLineItemProperties() {
    return {
      _source: "fuel_calculator",
      _fuel_calculator: "true",
    };
  }

  getAddToCartTrackingPayload() {
    const firstTracking = this.getTrackingMeta(this.getSelectionEntity(0));
    const secondTracking = this.getTrackingMeta(this.getSelectionEntity(1));
    const sessionTracking = this.getTrackingMeta(this.getSelectionEntity(2));

    return {
      sport_key: firstTracking.key,
      sport_key_local: firstTracking.local_key,
      threshold_key: secondTracking.key,
      threshold_key_local: secondTracking.local_key,
      session_key: sessionTracking.key,
      session_key_local: sessionTracking.local_key,
    };
  }

  setButtonLoadingState(button, loading) {
    if (!button) return;

    button.disabled = loading;
    button.classList.toggle("is-loading", loading);

    if (loading) {
      button.setAttribute("aria-busy", "true");
      return;
    }

    button.removeAttribute("aria-busy");
  }

  async requestCartAdd(items) {
    const response = await fetch("/cart/add.json", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items,
        sections: "cart-icon-bubble,cart-drawer",
        sections_url: window.location.pathname,
      }),
    });

    if (!response.ok) {
      throw new Error(`Cart add failed with status ${response.status}`);
    }

    return response.json();
  }

  refreshHeaderCartBubble(cartData) {
    const bubble = document.getElementById("cart-icon-bubble");
    const sectionHtml = cartData?.sections?.["cart-icon-bubble"];
    if (!bubble || !sectionHtml) return;

    const parsed = new DOMParser().parseFromString(sectionHtml, "text/html");
    const sectionRoot = parsed.querySelector(".shopify-section");
    if (!sectionRoot) return;

    bubble.innerHTML = sectionRoot.innerHTML;
  }

  refreshCartAndOpen(cartData) {
    console.log(cartData);
    const event = new CustomEvent("cart:refreshAndOpen", {
      detail: { cartData },
    });
    document.dispatchEvent(event);
  }

  animateHeaderCartBubble() {
    const cartBubble = document.querySelector(
      "#cart-icon-bubble .cart-count-bubble",
    );
    if (!cartBubble) return;

    cartBubble.classList.remove("fuel-calculator-cart-count-pop");
    void cartBubble.offsetWidth;
    cartBubble.classList.add("fuel-calculator-cart-count-pop");
  }

  async addSelectedItemsToCart() {
    if (!this.state.session) return;

    const products = this.getSessionPairs(this.state.session);
    const lineItemProperties = this.getFuelLineItemProperties();

    const items = products
      .filter((product) => product.variantId && product.inStock !== false)
      .map((product) => {
        return {
          id: Number(product.variantId),
          quantity: 1,
          properties: lineItemProperties,
        };
      });

    if (!items.length) return;

    const trackingPayload = this.getAddToCartTrackingPayload();

    this.publishFuelEvent("fuel_calculator_add_to_cart", {
      ...trackingPayload,
      itemCount: items.length,
      add_mode: "all",
      status: "clicked",
    });

    this.setButtonLoadingState(this.addToCartBtn, true);

    try {
      const cartData = await this.requestCartAdd(items);
      this.refreshHeaderCartBubble(cartData);
      this.refreshCartAndOpen(cartData);
      this.animateHeaderCartBubble();

      this.publishFuelEvent("fuel_calculator_add_to_cart_success", {
        ...trackingPayload,
        itemCount: items.length,
        add_mode: "all",
        status: "success",
      });

      if (
        typeof publish === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "fuel-calculator",
          cartData,
        });
      }
    } catch (error) {
      console.error("Failed to add fuel calculator products", error);

      this.publishFuelEvent("fuel_calculator_add_to_cart_failed", {
        ...trackingPayload,
        itemCount: items.length,
        add_mode: "all",
        status: "failed",
        message: error && error.message ? error.message : "unknown_error",
      });
    } finally {
      this.setButtonLoadingState(this.addToCartBtn, false);
    }
  }

  async onProductAddClick(event) {
    const button = event.target.closest("[data-fuel-product-add]");
    if (!button || !this.productsList.contains(button)) return;
    if (!this.state.session || button.disabled) return;

    const variantId = Number(button.dataset.variantId);
    if (!Number.isFinite(variantId)) return;

    const quantity = this.parseQty(button.dataset.quantity);
    const items = [
      {
        id: variantId,
        quantity,
        properties: this.getFuelLineItemProperties(),
      },
    ];

    const trackingPayload = this.getAddToCartTrackingPayload();

    this.publishFuelEvent("fuel_calculator_add_to_cart", {
      ...trackingPayload,
      itemCount: items.length,
      add_mode: "single",
      variant_id: variantId,
      quantity,
      status: "clicked",
    });

    this.setButtonLoadingState(button, true);

    try {
      const cartData = await this.requestCartAdd(items);
      this.refreshHeaderCartBubble(cartData);
      this.refreshCartAndOpen(cartData);
      this.animateHeaderCartBubble();

      this.publishFuelEvent("fuel_calculator_add_to_cart_success", {
        ...trackingPayload,
        itemCount: items.length,
        add_mode: "single",
        variant_id: variantId,
        quantity,
        status: "success",
      });

      if (
        typeof publish === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "fuel-calculator",
          cartData,
        });
      }
    } catch (error) {
      console.error("Failed to add single fuel calculator product", error);

      this.publishFuelEvent("fuel_calculator_add_to_cart_failed", {
        ...trackingPayload,
        itemCount: items.length,
        add_mode: "single",
        variant_id: variantId,
        quantity,
        status: "failed",
        message: error && error.message ? error.message : "unknown_error",
      });
    } finally {
      this.setButtonLoadingState(button, false);
    }
  }

  resetAll() {
    this.clearSelectionsFrom(0);
    this.resetResult();
    this.loadStepOptions(0);
    this.showStepByIndex(0);

    this.publishFuelEvent("fuel_calculator_retry", {
      step: 1,
    });
  }
}

if (!customElements.get("fuel-calculator-widget")) {
  customElements.define("fuel-calculator-widget", FuelCalculatorWidget);
}
